import { useEffect, useMemo, useState } from "react";
import { presalesApi } from "../../api/presalesApi";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")} ${d.toLocaleString("en", { month: "short" })}`;
}

function ModuleRow({ mod, onStateChange }) {
  const cycle = { in: "opt", opt: "out", out: "in" };
  const label = mod.state === "in" ? "In Scope" : mod.state === "opt" ? "Optional" : "Out";
  return (
    <div className="ps-mod-row">
      <div className="ps-mod-main">
        <span className={`ps-mod-toggle ${mod.state}`}>{mod.state === "in" ? "✓" : mod.state === "opt" ? "+" : "–"}</span>
        <span className="ps-mod-name">{mod.feature_name || mod.feature_id}</span>
      </div>
      <button
        className={`ps-ms-status ps-mod-status ${mod.state}`}
        title="Click to rotate state"
        onClick={() => onStateChange(mod.id, cycle[mod.state] || "in")}
      >
        {label}
      </button>
    </div>
  );
}

export default function ScopeTab({ lead, mutate, showToast }) {
  const sowProducts = lead.sow_products || [];
  const milestones  = lead.milestones   || [];
  const [products, setProducts] = useState([]);
  const [features, setFeatures] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [newProductId, setNewProductId] = useState("");
  const [addingProduct, setAddingProduct] = useState(false);
  const [showAddProductRow, setShowAddProductRow] = useState(false);
  const [moduleDraftByProduct, setModuleDraftByProduct] = useState({});
  const [addingModuleForProduct, setAddingModuleForProduct] = useState({});
  const [showAddModuleRowByProduct, setShowAddModuleRowByProduct] = useState({});

  const [assumptionsDraft, setAssumptionsDraft] = useState(null);
  const [savingAssumptions, setSavingAssumptions] = useState(false);

  const [addingMs, setAddingMs] = useState(false);
  const [msForm, setMsForm]     = useState({ title: "", milestone_date: "", status: "planned" });

  useEffect(() => {
    let mounted = true;
    (async () => {
      setMetaLoading(true);
      try {
        const [prodRes, featRes] = await Promise.all([
          presalesApi.listProducts(),
          presalesApi.listFeatures(),
        ]);
        if (!mounted) return;
        setProducts(Array.isArray(prodRes) ? prodRes : []);
        setFeatures(Array.isArray(featRes) ? featRes : []);
      } catch (err) {
        showToast?.(err.message || "Failed to load products/features", "error");
      } finally {
        if (mounted) setMetaLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [showToast]);

  const mappedProductIds = useMemo(() => {
    return new Set(
      sowProducts
        .map((p) => p.product_id)
        .filter(Boolean)
        .map(String)
    );
  }, [sowProducts]);

  const availableProducts = useMemo(() => {
    return products.filter((p) => !mappedProductIds.has(String(p.id || p.product_id)));
  }, [products, mappedProductIds]);

  const scopeCounts = useMemo(() => {
    const allModules = sowProducts.flatMap((p) => p.modules || []);
    return {
      in: allModules.filter((m) => m.state === "in").length,
      opt: allModules.filter((m) => m.state === "opt").length,
      out: allModules.filter((m) => m.state === "out").length,
      products: sowProducts.length,
    };
  }, [sowProducts]);

  async function patchModState(moduleId, state) {
    try {
      await presalesApi.patchSowModule(moduleId, { state });
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to update module", "error");
    }
  }

  async function saveAssumptions() {
    setSavingAssumptions(true);
    try {
      await presalesApi.updateLead(lead.id, { sow_assumptions: assumptionsDraft });
      setAssumptionsDraft(null);
      await mutate();
      showToast?.("Assumptions saved", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to save", "error");
    } finally {
      setSavingAssumptions(false);
    }
  }

  async function addSowProduct() {
    if (!newProductId) return;
    setAddingProduct(true);
    try {
      await presalesApi.addSowProduct(lead.id, { product_id: newProductId });
      setNewProductId("");
      setShowAddProductRow(false);
      await mutate();
      showToast?.("Product added to SOW", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to add SOW product", "error");
    } finally {
      setAddingProduct(false);
    }
  }

  async function removeSowProduct(sowProductId) {
    try {
      await presalesApi.deleteSowProduct(sowProductId);
      await mutate();
      showToast?.("Product removed from SOW", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to remove SOW product", "error");
    }
  }

  function getFeatureOptionsForProduct(prod) {
    const productId = String(prod.product_id || "");
    const existing = new Set((prod.modules || []).map((m) => String(m.feature_id)).filter(Boolean));
    return features.filter((f) => {
      const fPid = String(f.product_id || "");
      if (!fPid || !productId) return false;
      if (fPid !== productId) return false;
      const fid = String(f.id || f.feature_id || "");
      if (!fid) return false;
      return !existing.has(fid);
    });
  }

  function getTotalFeaturesForProduct(prod) {
    const productId = String(prod.product_id || "");
    return features.filter((f) => String(f.product_id || "") === productId).length;
  }

  async function addSowModule(sowProductId) {
    const featureId = moduleDraftByProduct[sowProductId];
    if (!featureId) return;
    setAddingModuleForProduct((p) => ({ ...p, [sowProductId]: true }));
    try {
      await presalesApi.addSowModule(sowProductId, { feature_id: featureId, state: "in" });
      setModuleDraftByProduct((p) => ({ ...p, [sowProductId]: "" }));
      await mutate();
      showToast?.("Module added", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to add module", "error");
    } finally {
      setAddingModuleForProduct((p) => ({ ...p, [sowProductId]: false }));
    }
  }

  async function removeSowModule(moduleId) {
    try {
      await presalesApi.deleteSowModule(moduleId);
      await mutate();
      showToast?.("Module removed", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to remove module", "error");
    }
  }

  async function addMilestone() {
    if (!msForm.title || !msForm.milestone_date) return;
    try {
      await presalesApi.addMilestone(lead.id, msForm);
      setMsForm({ title: "", milestone_date: "", status: "planned" });
      setAddingMs(false);
      await mutate();
      showToast?.("Milestone added", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to add milestone", "error");
    }
  }

  async function cycleMsStatus(ms) {
    const CYCLE = { planned: "next", next: "done", done: "planned" };
    try {
      await presalesApi.patchMilestone(ms.id, { status: CYCLE[ms.status] });
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to update milestone", "error");
    }
  }

  async function deleteMs(msId) {
    try {
      await presalesApi.deleteMilestone(msId);
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to delete", "error");
    }
  }

  return (
    <div>
      {/* SOW products + modules */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">
          <span>Statement of Work</span>
          <button onClick={() => setShowAddProductRow((v) => !v)} disabled={addingProduct}>
            {showAddProductRow ? "Cancel" : "+ Add Product"}
          </button>
        </div>
        <div className="ps-sow-summary">
          <div className="ps-sow-chip in">{scopeCounts.in} In Scope</div>
          <div className="ps-sow-chip opt">{scopeCounts.opt} Optional</div>
          <div className="ps-sow-chip out">{scopeCounts.out} Out</div>
          <div className="ps-sow-products-count">{scopeCounts.products} product{scopeCounts.products === 1 ? "" : "s"}</div>
        </div>
        {showAddProductRow && (
          <div className="ps-add-row ps-sow-add-row">
            <select
              className="ps-add-input"
              value={newProductId}
              onChange={(e) => setNewProductId(e.target.value)}
              disabled={metaLoading || addingProduct}
            >
              <option value="">Select product…</option>
              {availableProducts.map((p) => (
                <option key={p.id || p.product_id || p.slug || p.name} value={p.id || p.product_id || ""}>
                  {p.name || p.product_name || p.slug || "Unnamed"}
                </option>
              ))}
            </select>
            <button className="ps-add-btn" onClick={addSowProduct} disabled={!newProductId || addingProduct}>
              {addingProduct ? "Adding…" : "Add Product"}
            </button>
          </div>
        )}
        {sowProducts.length === 0 ? (
          <div className="ps-so-empty">No products added to SOW</div>
        ) : (
          <div className="ps-prod-list">
            {sowProducts.map((prod) => {
              const inScopeCount = (prod.modules || []).filter((m) => m.state === "in").length;
              const totalFeatures = getTotalFeaturesForProduct(prod);
              return (
              <div key={prod.id} className="ps-prod-group">
                <div className="so-prod-head">
                  <div className="so-prod-name">
                    <span className="dot" data-p={(prod.product_slug || "").toLowerCase()} />
                    <span className="lbl">{prod.product_name || prod.product_id}</span>
                    <span className="cnt">{inScopeCount} / {totalFeatures}</span>
                  </div>
                  <div className="so-prod-actions">
                    <button
                      className="so-prod-add"
                      type="button"
                      onClick={() => setShowAddModuleRowByProduct((p) => ({ ...p, [prod.id]: !p[prod.id] }))}
                    >
                      ＋ Add module
                    </button>
                  </div>
                </div>
                {(prod.modules || []).map((mod) => (
                  <div key={mod.id} className="ps-mod-wrap">
                    <ModuleRow mod={mod} onStateChange={patchModState} />
                  </div>
                ))}
                {(prod.modules || []).length === 0 && (
                  <div className="ps-mod-empty">
                    No modules
                  </div>
                )}
                {showAddModuleRowByProduct[prod.id] && (
                  <div className="ps-add-row ps-mod-add-row">
                    <select
                      className="ps-add-input"
                      value={moduleDraftByProduct[prod.id] || ""}
                      onChange={(e) => setModuleDraftByProduct((p) => ({ ...p, [prod.id]: e.target.value }))}
                      disabled={metaLoading || addingModuleForProduct[prod.id]}
                    >
                      <option value="">Select module/feature…</option>
                      {getFeatureOptionsForProduct(prod).map((f) => (
                        <option key={f.id || f.feature_id} value={f.id || f.feature_id}>
                          {f.feature_name || f.name || "Unnamed feature"}
                        </option>
                      ))}
                    </select>
                    <button
                      className="ps-add-btn"
                      onClick={() => addSowModule(prod.id)}
                      disabled={!moduleDraftByProduct[prod.id] || !!addingModuleForProduct[prod.id]}
                    >
                      {addingModuleForProduct[prod.id] ? "Adding…" : "Add Module"}
                    </button>
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
      </div>

      {/* Milestones */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">
          <span>Milestones</span>
          <button onClick={() => setAddingMs(v => !v)}>+ Add</button>
        </div>
        {addingMs && (
          <div className="ps-add-row" style={{ marginBottom: 12 }}>
            <input
              type="text"
              className="ps-add-input"
              placeholder="Milestone title…"
              value={msForm.title}
              onChange={(e) => setMsForm(p => ({ ...p, title: e.target.value }))}
            />
            <input
              type="date"
              className="ps-add-input"
              style={{ flex: "0 0 140px" }}
              value={msForm.milestone_date}
              onChange={(e) => setMsForm(p => ({ ...p, milestone_date: e.target.value }))}
            />
            <button className="ps-add-btn" disabled={!msForm.title || !msForm.milestone_date} onClick={addMilestone}>
              Add
            </button>
            <button className="ps-add-cancel-btn" onClick={() => setAddingMs(false)}>Cancel</button>
          </div>
        )}
        {milestones.length === 0 ? (
          <div className="ps-so-empty">No milestones</div>
        ) : (
          <div className="ps-ms-list">
            {milestones.map((ms) => (
              <div key={ms.id} className="ps-ms-row">
                <span className="ps-ms-when">{fmtDate(ms.milestone_date)}</span>
                <span className="ps-ms-t">{ms.title}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    className={`ps-ms-status ${ms.status}`}
                    title="Click to cycle status"
                    onClick={() => cycleMsStatus(ms)}
                  >
                    {ms.status}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assumptions */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">Assumptions &amp; Constraints</div>
        <textarea
          className="ps-notes-area"
          value={assumptionsDraft !== null ? assumptionsDraft : (lead.sow_assumptions || "")}
          onChange={(e) => setAssumptionsDraft(e.target.value)}
          placeholder="Enter assumptions…"
        />
        {assumptionsDraft !== null && (
          <div className="ps-notes-save-bar">
            <button onClick={() => setAssumptionsDraft(null)}>Cancel</button>
            <button className="primary" disabled={savingAssumptions} onClick={saveAssumptions}>
              {savingAssumptions ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
